import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  useDropdownItemsCtxProvider,
  useModalConfirm,
  useRidesJourneyActions,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  ConfirmResult,
  DrawerType,
  IJourneyDuplicateFormFields,
  JourneysCreationTypeEnum,
  RydeUiUnit,
  useDrawers,
} from "@ryde/shared";

import { useJourneyDuplicateResolver } from "./use-journey-duplicate-resolver";

const entity: RydeUiUnit = "journey";

export const useJourneyDuplicate = () => {
  const { confirmDialogResult } = useModalConfirm();
  const { journeyDuplicateFormValidationSchema } =
    useJourneyDuplicateResolver();
  const { t } = useTranslation();

  const resolver = useYupValidationResolver(
    journeyDuplicateFormValidationSchema,
    "journey",
  );

  const { closeDrawer } = useDrawers();
  useDropdownItemsCtxProvider();

  const { bulkCloneJourneys } = useRidesJourneyActions();

  const methods = useForm<IJourneyDuplicateFormFields>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      dayGroup: [],
      dayType: [],
      creationSource: JourneysCreationTypeEnum.rideDuplication,
    },
    resolver,
  });

  const {
    getValues,
    trigger,
    formState: { errors, isValid },
  } = methods;

  const isSaveDisabled = Boolean(!isValid || Object.keys(errors || {}).length);

  const onSaveClick = useEvent(async (journeyId: number): Promise<void> => {
    await trigger();

    if (!isValid) {
      return;
    }

    const confirmationResult = await confirmDialogResult({
      title: t("general.DUPLICATE_CONFIRM"),
      message: t(`general.DUPLICATE_MESSAGE`, {
        entity: t(`${entity}.single`),
      }),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    const { name, creationSource, dayType, dayGroup, dateFrom, dateTo } =
      getValues();

    const response = await bulkCloneJourneys({
      ids: [journeyId],
      name,
      creationSource,
      dateFrom: dateFrom && dayjs(dateFrom).toISOString(),
      dateTo: dateTo && dayjs(dateTo).toISOString(),
      dayGroup: dayGroup ?? [],
      dayType: dayType ?? [],
    });

    if (response?.success) {
      closeDrawer(DrawerType.JourneyDuplicate);
    }
  });

  return {
    methods,
    errors,
    isSaveDisabled,
    onSaveClick,
  };
};
